import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { PictoContrats } from '@/assets/icons-april-fr/pictos';
import AprilPicto from '@/components/shared/april-picto';
import { Button } from '@/components/ui/button';
import { useContract, useContractsData, useContracts, useSelectedContract } from '@/hooks/api/use-contracts';
import type { Contract } from '@/types/api/contract';
import { getDefaultContract } from '@/lib/last-selected-contract';

import ContractPickerModal from '@/components/shared/contract-picker-modal';
import SmallSpinner from '@/components/shared/spinners/small-spinner';

export interface SelectContractProps {
    children?: ReactNode;
    /** Masque le bandeau de choix du contrat (pages `/frames/*`). */
    isFrame?: boolean;
    /** Restreint les contrats proposés. */
    filterFn?: (contract: Contract) => boolean;
}

const SelectContractContent = (props: SelectContractProps) => {
    const { children, isFrame, filterFn } = props;
    const allContracts = useContractsData();
    const contracts = useMemo(() => (filterFn ? allContracts?.filter(filterFn) : allContracts), [allContracts, filterFn]);
    const [showModal, setShowModal] = useState(false);
    const [stateContract, setCurrentContract] = useState<Contract | undefined>(getDefaultContract(contracts));
    const currentContract: Partial<Contract> = stateContract ?? { vehicule: { marque: '' } };
    /* Contrat chargé en détail : il devient le contrat sélectionné de l'application. */
    const [contractId, setContractId] = useState<string | number | undefined>(getDefaultContract(contracts)?.id);
    const contractQuery = useContract(contractId);
    /* Dernier contrat chargé en détail, éventuellement par le contenu affiché. */
    const contract = useSelectedContract();

    useEffect(() => {
        const defaultContract = getDefaultContract(contracts);
        if (defaultContract?.id) {
            setContractId(defaultContract.id);
        }
    }, [contracts]);

    useEffect(() => {
        if (contract?.id && currentContract?.id !== contract.id) {
            setCurrentContract(contract);
        }
    }, [contract]);

    const selectContract = (c: Contract) => {
        setCurrentContract(c);
        setContractId(c.id);
    };

    /* Comme avant : contenu affiché une fois le détail chargé, ou en échec. */
    const contractLoaded = !contractQuery.isPending;
    const vehicule = currentContract.vehicule ?? {};

    return (
        <div>
            {!isFrame && (
                <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-3 p-6 rounded-lg border bg-muted/30 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 shrink-0">
                            <AprilPicto icon={PictoContrats} size="md" />
                        </div>
                        <div>
                            <p className="font-semibold text-sm">
                                Contrat n°{currentContract.numeroPolice}
                            </p>
                            {vehicule.marque && (
                                <p className="text-sm text-muted-foreground">
                                    {vehicule.marque} {vehicule.immatriculation || (vehicule.VIN as string | undefined)}
                                </p>
                            )}
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowModal(true)}
                        aria-haspopup="dialog"
                        aria-expanded={showModal}
                        className="w-full sm:w-auto cursor-pointer"
                    >
                        Changer de contrat
                        <ChevronDown className="w-4 h-4" aria-hidden="true" />
                    </Button>
                </div>
            )}

            <ContractPickerModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onSelect={selectContract}
                contracts={contracts}
                activeContractId={currentContract?.id}
                title="Changer de contrat"
            />

            <div>{contractLoaded ? children : <SmallSpinner />}</div>
        </div>
    );
};

/**
 * Bandeau de choix du contrat, au-dessus d'un contenu qui dépend du contrat sélectionné
 * (`useSelectedContract`). Le contrat par défaut est le dernier choisi, sinon le premier.
 */
const SelectContract = (props: SelectContractProps) => {
    const contracts = useContracts();

    if (contracts.isPending) return <SmallSpinner />;

    return <SelectContractContent {...props} />;
};

export default SelectContract;
